import { Fragment, useState } from "react";
import { CheckSquare, Copy, Link2, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { UserAvatar } from "@/components/UserAvatar";
import { useChat } from "@/contexts/ChatContext";
import { getChatAdjuntoUrl } from "@/lib/api";
import { formatDia, formatHora, datosFiscalesTexto, nombreVisible } from "@/lib/clienteNombre";
import { AdjuntoMensaje } from "@/components/chat/Adjuntos";
import { RichText, extraerUrls } from "@/components/chat/RichText";
import { cn } from "@/lib/utils";

const AGRUPAR_MS = 5 * 60 * 1000;

function Adjuntos({ adjuntos, propio }) {
  const imagenes = adjuntos.filter((a) => a.es_imagen);
  const archivos = adjuntos.filter((a) => !a.es_imagen);
  return (
    <div className="space-y-1.5">
      {imagenes.length > 0 && (
        <div className={cn("grid gap-1", imagenes.length > 1 ? "grid-cols-2" : "grid-cols-1")}>
          {imagenes.map((a) => (
            <a key={a.id} href={getChatAdjuntoUrl(a.id)} target="_blank" rel="noopener noreferrer" className="block overflow-hidden rounded-lg bg-black/5">
              <img src={getChatAdjuntoUrl(a.id)} alt={a.nombre} loading="lazy" className={cn("w-full object-cover", imagenes.length > 1 ? "h-32" : "max-h-72")} />
            </a>
          ))}
        </div>
      )}
      {archivos.map((a) => (
        <AdjuntoMensaje key={a.id} adjunto={a} propio={propio} />
      ))}
    </div>
  );
}

function TarjetaCliente({ cliente }) {
  if (!cliente) return null;
  const texto = datosFiscalesTexto(cliente);
  return (
    <div className="rounded-lg bg-background text-foreground border p-3 text-xs space-y-2 min-w-[220px]">
      <p className="font-medium text-sm">Datos fiscales · {nombreVisible(cliente)}</p>
      <pre className="whitespace-pre-wrap font-sans leading-relaxed text-muted-foreground">{texto}</pre>
      <button
        type="button"
        onClick={() => navigator.clipboard.writeText(texto).then(() => toast.success("Datos fiscales copiados"), () => toast.error("No se pudo copiar"))}
        className="flex items-center gap-1.5 text-primary font-medium"
      >
        <Copy className="w-3.5 h-3.5" /> Copiar
      </button>
    </div>
  );
}

/** Hueco invisible al final del texto del ancho de la hora: si no cabe en la última línea, salta a una nueva. */
function EspacioHora({ editado }) {
  return <span aria-hidden className="inline-block h-3 align-baseline" style={{ width: editado ? 88 : 42 }} />;
}

function Burbuja({ m, propio, agrupado, autor, cliente, onCrearTrabajo, onFijarEnlace, onEditar, onBorrar }) {
  const { usuariosByEmail } = useChat();
  const [editando, setEditando] = useState(false);
  const [draft, setDraft] = useState(m.texto);
  const nombresMencion = (m.menciones || []).map((e) => usuariosByEmail[e]?.nombre).filter(Boolean);
  const urls = extraerUrls(m.texto);
  const horaEnLinea = !editando && (m.eliminado || !!m.texto);

  const guardarEdicion = async () => {
    const t = draft.trim();
    setEditando(false);
    if (t && t !== m.texto) await onEditar(m, t);
  };

  return (
    <div className={cn("group flex gap-2 px-3 md:px-6", propio ? "justify-end" : "justify-start", agrupado ? "mt-0.5" : "mt-3")}>
      {!propio && (
        <div className="w-8 shrink-0">
          {!agrupado && <UserAvatar user={autor} size={32} />}
        </div>
      )}
      <div className={cn("flex items-end gap-1 min-w-0 max-w-[85%] md:max-w-[70%]", propio && "flex-row-reverse")}>
        <div
          className={cn(
            "relative min-w-0 max-w-full rounded-2xl px-3 py-2 text-sm shadow-sm",
            propio ? "bg-primary text-primary-foreground" : "bg-background border",
            propio ? (agrupado ? "rounded-tr-md" : "rounded-tr-sm") : agrupado ? "rounded-tl-md" : "rounded-tl-sm",
            m.eliminado && "opacity-60 italic"
          )}
        >
          {!propio && !agrupado && <p className="text-xs font-semibold mb-0.5" style={{ color: autor?.color }}>{autor?.nombre}</p>}
          {m.eliminado ? (
            <p>
              Mensaje eliminado
              <EspacioHora editado={false} />
            </p>
          ) : editando ? (
            <Textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  guardarEdicion();
                }
                if (e.key === "Escape") {
                  setDraft(m.texto);
                  setEditando(false);
                }
              }}
              onBlur={guardarEdicion}
              className="min-w-[240px] text-sm text-foreground bg-background"
            />
          ) : (
            <div className="space-y-1.5">
              {m.tipo === "tarjeta_cliente" && <TarjetaCliente cliente={cliente} />}
              {m.adjuntos?.length > 0 && <Adjuntos adjuntos={m.adjuntos} propio={propio} />}
              {m.texto && (
                <p>
                  <RichText texto={m.texto} nombresMencion={nombresMencion} propio={propio} />
                  <EspacioHora editado={m.editado} />
                </p>
              )}
            </div>
          )}
          {/* Hora como en WhatsApp: en la misma línea que el texto (o en la última), siempre a la derecha */}
          <span
            className={cn(
              "text-[10px] tabular-nums whitespace-nowrap",
              horaEnLinea ? "absolute right-3 bottom-1.5" : "block text-right mt-0.5",
              propio ? "text-primary-foreground/70" : "text-muted-foreground"
            )}
          >
            {m.editado && !m.eliminado ? "editado · " : ""}
            {formatHora(m.fecha_creacion)}
          </span>
        </div>
        {!m.eliminado && !editando && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground opacity-40 md:opacity-0 group-hover:opacity-100 hover:bg-muted data-[state=open]:opacity-100" aria-label="Acciones">
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={propio ? "end" : "start"} className="w-56">
              <DropdownMenuItem onSelect={() => onCrearTrabajo(m)}>
                <CheckSquare className="w-4 h-4 mr-2" /> Crear trabajo con esto
              </DropdownMenuItem>
              {urls.length > 0 && (
                <DropdownMenuItem onSelect={() => onFijarEnlace(urls[0])}>
                  <Link2 className="w-4 h-4 mr-2" /> Guardar enlace en la ficha
                </DropdownMenuItem>
              )}
              {m.texto && (
                <DropdownMenuItem onSelect={() => navigator.clipboard.writeText(m.texto).then(() => toast.success("Copiado"))}>
                  <Copy className="w-4 h-4 mr-2" /> Copiar texto
                </DropdownMenuItem>
              )}
              {propio && (
                <>
                  <DropdownMenuSeparator />
                  {m.tipo === "texto" && m.texto && (
                    <DropdownMenuItem
                      onSelect={() => {
                        setDraft(m.texto);
                        setEditando(true);
                      }}
                    >
                      <Pencil className="w-4 h-4 mr-2" /> Editar
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onSelect={() => onBorrar(m)} className="text-destructive focus:text-destructive">
                    <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

/**
 * Hilo de mensajes: separadores por día, mensajes agrupados por autor, mensajes de sistema centrados
 * y marca de «mensajes nuevos» en el primer no leído.
 */
export function MessageList({ mensajes, yo, cliente, primerNoLeidoId, onAbrirTrabajo, ...acciones }) {
  const { usuariosByEmail } = useChat();
  return (
    <div className="pb-3">
      {mensajes.map((m, i) => {
        const prev = mensajes[i - 1];
        const nuevoDia = !prev || new Date(prev.fecha_creacion).toDateString() !== new Date(m.fecha_creacion).toDateString();
        const agrupado =
          !nuevoDia &&
          prev &&
          prev.tipo !== "sistema" &&
          m.tipo !== "sistema" &&
          prev.autor === m.autor &&
          new Date(m.fecha_creacion) - new Date(prev.fecha_creacion) < AGRUPAR_MS &&
          m.id !== primerNoLeidoId;
        const autor = usuariosByEmail[m.autor] || { email: m.autor, nombre: (m.autor || "").split("@")[0] };
        return (
          <Fragment key={m.id}>
            {nuevoDia && (
              <div className="flex justify-center my-4">
                <span className="text-[11px] text-muted-foreground bg-background/90 border rounded-full px-3 py-1 capitalize shadow-sm">{formatDia(m.fecha_creacion)}</span>
              </div>
            )}
            {m.id === primerNoLeidoId && (
              <div className="flex items-center gap-3 px-6 my-3" id="chat-primer-no-leido">
                <span className="h-px flex-1 bg-primary/30" />
                <span className="text-[11px] font-medium text-primary">Mensajes nuevos</span>
                <span className="h-px flex-1 bg-primary/30" />
              </div>
            )}
            {m.tipo === "sistema" ? (
              <div className="flex justify-center px-6 my-2">
                <button
                  type="button"
                  disabled={!m.trabajo_id}
                  onClick={() => m.trabajo_id && onAbrirTrabajo?.(m.trabajo_id)}
                  className={cn("text-[11px] text-muted-foreground bg-muted/70 rounded-full px-3 py-1 text-center max-w-full", m.trabajo_id && "hover:bg-muted hover:text-foreground")}
                >
                  {m.texto} · {formatHora(m.fecha_creacion)}
                </button>
              </div>
            ) : (
              <Burbuja m={m} propio={m.autor === yo} agrupado={agrupado} autor={autor} cliente={cliente} {...acciones} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}
